import React, { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Container,
  VStack,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Link,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { register } from "../api/auth";

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function SignupPage() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const emailTrim = email.trim();
  const usernameTrim = username.trim();

  const emailError = !emailTrim
    ? "Email is required."
    : !isValidEmail(emailTrim)
      ? "Please enter a valid email address."
      : "";

  const usernameError = !usernameTrim
    ? "Username is required."
    : usernameTrim.length < 3
      ? "Username must be at least 3 characters."
      : "";

  const passwordError = !password
    ? "Password is required."
    : password.length < 8
      ? "Password must be at least 8 characters."
      : "";

  const confirmPasswordError = !confirmPassword
    ? "Please confirm your password."
    : password !== confirmPassword
      ? "Passwords do not match."
      : "";

  async function onSubmit(e) {
    e.preventDefault();
    setApiError("");
    setSuccessMessage("");

    if (emailError || usernameError || passwordError || confirmPasswordError) {
      return;
    }

    try {
      setSubmitting(true);

      await register({
        email: emailTrim,
        username: usernameTrim,
        password,
      });

      setSuccessMessage("Account created successfully. Redirecting to login...");

      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Sign up failed.";

      setApiError(typeof msg === "string" ? msg : "Sign up failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <style>{`
        .poolBackground {
          background: linear-gradient(
            120deg,
            #0a3d62,
            #1e81b0,
            #3caea3,
            #1e81b0,
            #0a3d62
          );
          background-size: 400% 400%;
          animation: poolShimmer 8s ease infinite;
        }

        @keyframes poolShimmer {
          0% {
            background-position: 0% 50%;
            filter: brightness(100%);
          }
          50% {
            background-position: 100% 50%;
            filter: brightness(120%);
          }
          100% {
            background-position: 0% 50%;
            filter: brightness(100%);
          }
        }
      `}</style>

      <Box minH="100vh" className="poolBackground" color="white">
        <Box bg="rgba(0, 0, 40, 0.4)" minH="100vh" px={4} py={20}>
          <Container maxW="container.md" centerContent>
            <VStack spacing={8} align="stretch">
              <Box
                bg="#00007f"
                px={8}
                py={6}
                borderRadius="md"
                boxShadow="lg"
                textAlign="center"
              >
                <Heading size={{ base: "xl", md: "2xl" }}>The Deep End</Heading>
              </Box>

              <Box textAlign="center">
                <Text fontSize="xl">Create your account and jump in.</Text>
              </Box>

              <Box
                as="form"
                onSubmit={onSubmit}
                bg="rgba(255, 255, 255, 0.15)"
                px={6}
                py={8}
                borderRadius="md"
                boxShadow="xl"
                backdropFilter="blur(6px)"
              >
                <VStack spacing={4}>
                  {apiError ? (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {apiError}
                    </Alert>
                  ) : null}

                  {successMessage ? (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      {successMessage}
                    </Alert>
                  ) : null}

                  <FormControl isInvalid={!!emailError && email.length > 0}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      placeholder="Email"
                      type="email"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      autoComplete="email"
                    />
                    <FormErrorMessage>{emailError}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!usernameError && username.length > 0}>
                    <FormLabel>Username</FormLabel>
                    <Input
                      placeholder="Username"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                    />
                    <FormErrorMessage>{usernameError}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!passwordError && password.length > 0}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      placeholder="Password"
                      type="password"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <FormErrorMessage>{passwordError}</FormErrorMessage>
                  </FormControl>

                  <FormControl
                    isInvalid={!!confirmPasswordError && confirmPassword.length > 0}
                  >
                    <FormLabel>Confirm Password</FormLabel>
                    <Input
                      placeholder="Confirm Password"
                      type="password"
                      variant="filled"
                      bg="white"
                      color="black"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <FormErrorMessage>{confirmPasswordError}</FormErrorMessage>
                  </FormControl>

                  <Button
                    type="submit"
                    bg="#00007f"
                    color="white"
                    w="100%"
                    isLoading={submitting}
                    loadingText="Creating account"
                  >
                    Sign Up
                  </Button>

                  <Text fontSize="sm">
                    Already have an account?{" "}
                    <Link
                      as={RouterLink}
                      to="/login"
                      color="orange.200"
                      fontWeight="600"
                    >
                      Back to Login
                    </Link>
                  </Text>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
}